/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';
import {
  autosaveCreateEditorId,
  autosaveTinyMCEConfig,
} from '@linc-technologies/linced-common/utils/tinymce';
import moment from 'moment/moment';

export default class TopicManager extends Component {
  PostType = PostType;
  GoalState = GoalState;

  // Services
  @service
  features;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  sortProp = 'fullName';

  sortDir = 'asc';

  selectedGroups = [];

  @tracked
  isLoading = false;

  goalProgress = null;

  @tracked
  states = null;

  @tracked
  statesMap = null;

  @tracked
  topicManagerState = 0;

  // Computed property to determine the curent state of the topic
  @computed('topicId', 'statesMap', 'group.currentLearners.[]')
  get topicState() {
    if (!this.statesMap || !(this.group && this.group.currentLearners)) {
      return false;
    }
    let countCurrent = 0;
    let countComplete = 0;
    let countNotStarted = 0;
    let overall = 'notstarted';
    const statesMap = this.statesMap || {};
    const selectedLearners = this.selectedLearners || {};
    const topicId = this.topicId;
    // Check if all learners have state.awarded equal to 2 or 3
    selectedLearners.forEach((learner) => {
      const person = learner.person;
      const stateKey = `${topicId}-${person.id}`;
      let state = statesMap[stateKey];

      if (state) {
        if (state.awarded === 2) {
          countCurrent++;
        } else if (state.awarded === 3) {
          countComplete++;
        } else if (state.awarded === 1) {
          countNotStarted++;
        }
      } else {
        countNotStarted++;
      }
    });

    // If all students have a state and at least one is set to 2, return "current"
    if (countCurrent > 0) {
      if (
        countCurrent === selectedLearners.length ||
        countCurrent + countComplete === selectedLearners.length
      ) {
        overall = 'current';
      }
    }
    // If all students have a state set to 3, return "complete"
    if (countComplete === selectedLearners.length) {
      overall = 'complete';
    }

    return {
      countCurrent,
      countComplete,
      countNotStarted,
      overall,
    };
  }

  @tracked
  awarded = 2;

  group = null;

  @tracked
  topic = null;

  @tracked
  _postSubject = null;

  @action
  set_postSubject(value) {
    this._postSubject = value;
  }

  @computed('group.label', '_postSubject')
  get postSubject() {
    if (this._postSubject !== null) {
      return this._postSubject;
    }
    return (
      this.group.label + ' is starting a new ' + this.strings.singularTopicLabel
    );
  }

  @computed('topic.id')
  get topicId() {
    let topic = this.topic; // Get the topic object
    return topic ? topic.id : null; // Safely access topic.id
  }

  @tracked
  topicDialog = false;

  @alias('group.currentLearners')
  selectedLearners;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (this.topicDialog) {
      const group = this.group;
      this.selectedGroups = [...this.selectedGroups, group];
    }
  }

  get topicGoal() {
    let goal = this.topic || [];
    return this.groupTopicBySubcategory(goal);
  }

  @tracked
  saveInProgress = false;

  @tracked
  disableCancel = false;

  @tracked
  disableUpdate = false;

  @tracked
  disableNext = true;

  // Helper method to group goals by subcategory label
  groupTopicBySubcategory(goal) {
    let subcategoryLabel = goal.get('subcategory.label') || 'Other';
    let acc = [];
    if (!acc[subcategoryLabel]) {
      acc[subcategoryLabel] = [];
    }
    // Initialise the total count if it doesn't exist
    if (!acc.totalCount) {
      acc.totalCount = 0;
    }
    // Add the goal to the group, increment the subcategory count, and the total count
    acc[subcategoryLabel].push(goal);
    acc.totalCount += 1;
    return acc;
  }

  @alias('group.currentLearners.length')
  numLearners;

  @computed('goals.[]')
  get milestoneSubjects() {
    const goals = this.goals;

    const uniqueSubjects = new Map(); // Use Map to ensure uniqueness by `id`

    goals.forEach((goal) => {
      const subject = goal.subject;
      if (subject && subject.get('trackable')) {
        // Use .get() for Ember objects
        uniqueSubjects.set(subject.get('id'), {
          id: subject.get('id'),
          label: subject.get('label'),
        });
      }
    });

    // Return the unique values as an array of objects
    return Array.from(uniqueSubjects.values());
  }

  // Community Post creation
  postType = PostType.GLOBAL_NOTICE;

  entityType = 'posts/communitynotice';

  // TinyMCE editor
  // editorOptions returns an object for TinyMCE editor config with autosave enabled.
  // We need a unique element ID for specifying selectors and localStorage keys.
  @computed('editorId')
  get editorOptions() {
    return Object.freeze(autosaveTinyMCEConfig(this.editorId));
  }

  @computed(
    'session.person.id',
    'selectedPersonIdPrefix',
    'selectedPeoplePrefix',
    'additionalAutosavePrefix',
  )
  get editorId() {
    let userId = this.session.person.get('id');

    return autosaveCreateEditorId(
      userId,
      this.selectedPersonIdPrefix,
      this.additionalAutosavePrefix,
    );
  }

  // the following prefixes are used in editorId
  // if a parent component or route adds it for more uniqueness.
  @tracked
  selectedPersonIdPrefix = '';

  @tracked
  additionalAutosavePrefix = '';

  @tracked
  goalDialogOpen;

  @tracked
  postBody;

  @action
  setPostBody(value) {
    this.postBody = value;
  }

  @tracked
  postDate;

  @tracked
  selectedGoal;

  @tracked
  selectedGoalStates;

  @action
  async initialiseComponent() {
    this.isLoading = true;

    try {
      this.refreshStates();
    } finally {
      this.isLoading = false;
    }
  }

  @action
  startTopic() {
    this.topicDialog = true;
    this.topicManagerState = 1;
    this.awarded = 2;
    this.addCustomHtml(2);
    this.initialiseComponent();
  }

  @action
  endTopic() {
    this.topicDialog = true;
    this.topicManagerState = 2;
    this.awarded = 3;
    this.addCustomHtml(3);
    this.initialiseComponent();
  }

  @action
  addCustomHtml() {
    let customHtml = '';
    const topic = this.topic;
    const linkedGoals = topic.get('linkedGoals') || [];
    if (this.topicManagerState === 2) {
      customHtml += `<p>Our group has finished "${topic.educatorDescription}"</p>`;
      customHtml += `<p>As part of this, your child worked on the following:</p>`;
    } else {
      customHtml += `<p>Our group is starting "${topic.educatorDescription}"</p>`;
      customHtml += `<p>As part of this, your child will be working on the following:</p>`;
    }

    customHtml += '<ul>';
    linkedGoals.forEach((goal) => {
      customHtml += `<li>${goal.get('peopleDescription')}</li>`;
    });
    customHtml += '</ul>';

    // Safely mark as HTML
    this.postBody = customHtml;
  }

  @action
  closeTopicDialog() {
    this.topicDialog = false;
    this.awarded = 2;
  }

  @action
  openGoalDialog(goal) {
    this.selectedGoal = goal;
    this.selectedGoalStates = this.store.query('goals/state', {
      groupId: this.group.id,
      goalId: goal.get('id'),
      current: true,
    });
    this.goalDialogOpen = true;
  }

  @action
  closeGoalDialog() {
    this.goalDialogOpen = false;
    this.initialiseComponent();
  }

  @action
  bulkApplyCoreGoals() {
    this.disableCancel = true;
    this.saveInProgress = true;
    this.disableUpdate = true;

    // Retrieve linkedGoals and ensure it is an array
    const coreGoals = this.goals || [];

    if (!coreGoals.length) {
      this.toaster.error('No core goals available to apply.');

      return;
    }
    // Collect promises for bulk apply operation
    const bulkApplyPromises = coreGoals.map((goal) => {
      return new Promise((resolve, reject) => {
        this.bulkApply(goal, resolve, reject);
      });
    });

    Promise.allSettled(bulkApplyPromises)
      .then((results) => {
        const hasErrors = results.some(
          (result) => result.status === 'rejected',
        );
        if (hasErrors) {
          this.toaster.error('Bulk update for all goals failed.');
        } else {
          this.toaster.success('Bulk update for all goals successful.');
        }
        this.disableCancel = true;
        this.disableNext = false;
        this.disableUpdate = false;
        this.refreshStates();
      })
      .finally(() => {
        this.saveInProgress = false;
        this.disableCancel = true;
        this.disableUpdate = false;
        this.disableNext = false;
      });
  }

  @action
  bulkApply(goal, resolve, reject) {
    const learners = this.selectedLearners || [];
    const statesMap = this.statesMap || {};
    const awarded = this.awarded;
    Promise.all(
      learners.map((learner) => {
        const person = learner.person;
        const key = `${goal.id}-${person.id}`;
        const goalState = statesMap[key];
        let grade = null;
        // If no `goalState` exists, create a new one
        if (goalState) {
          if (goalState.awarded === awarded) {
            return Promise.resolve();
          }
          if (goalState.awarded === GoalState.SECURE) {
            return Promise.resolve();
          }
          grade = goalState.grade || null;
        }

        const stateParams = {
          schoolId: this.get('session.school'),
          assessorId: this.get('session.person'),
          person,
          goal,
          awarded,
          grade,
        };

        const newState = this.store.createRecord('goals/state', stateParams);

        // After saving the new state
        return saveOrDiscard(newState).catch(() => {
          throw new Error(`Save failed for learner ID: ${person.id}`);
        });
      }),
    )
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  }

  @action
  refreshStates() {
    // Check if `this.goals` exists and is an array
    if (!Array.isArray(this.goals) || this.goals.length === 0) {
      this.states = []; // Reset states to an empty array
      this.statesMap = {}; // Reset statesMap to an empty object
      return;
    }

    Promise.all(
      this.goals.map(async (goal) => {
        const states = await goal.get('states');
        return states.slice(); // Ensure we work with a standard array
      }),
    )
      .then((allStates) => {
        const states = allStates.flat();
        // Ensure each state has valid `goal_id` and `person_id`
        const validStates = states.filter(
          (state) => state.goal_id && state.person_id,
        );

        // Build statesMap
        const statesMap = validStates.reduce((map, state) => {
          const key = `${state.goal_id}-${state.person_id}`;
          map[key] = state;
          return map;
        }, {});

        this.states = validStates; // Save valid states
        this.statesMap = statesMap; // Save the map
      })
      .catch(() => {
        this.toaster.error('Failed to refresh states. Please try again.');
      })
      .finally(() => {});
  }

  @action
  saveAndStartTopic(status) {
    // Call the savePost logic
    if (status === 'none') {
      this.refreshStates();
      this.bulkApplyCoreGoals();
      this.topicDialog = false;
      this.awarded = 2;
      this.topicManagerState = 0;
    } else {
      this.refreshStates();
      this.bulkApplyCoreGoals();
      this.savePost(status);
      this.topicDialog = false;
      this.awarded = 2;
      this.topicManagerState = 0;
    }
  }

  @action
  saveAndEndTopic() {
    this.refreshStates();
    this.topicDialog = false;
    this.awarded = 2;
    this.topicManagerState = 0;
  }

  savePost(status) {
    let setPostStatus = PostStatus.DRAFT;
    if (status === 'publish') {
      setPostStatus = PostStatus.PUBLISHED;
    }
    this.postDate = moment().unix();
    const group = this.group;
    const selectedGroups = [];
    selectedGroups.push(group);
    let post = this.store.createRecord('posts/communitynotice', {
      postType: PostType.COMMUNITY_NOTICE,
      status: setPostStatus,
      schoolId: this.get('session.school'),
      groupIds: selectedGroups,
      authorId: this.get('session.person'),
      content: this.postBody,
      subject: this.postSubject,
      displayDate: this.postDate,
      topicId: this.topic,
    });
    post.save().then((post) => {
      post.set('status', setPostStatus);
      post
        .save()
        .then(() => {
          this.toaster.success('Community Notice published successfully.');
        })
        .catch(() => {
          this.toaster.error('Community Notice publishing failed.');
        });
    });
  }

  didInsertElement() {
    super.didInsertElement(...arguments);
    this.initialiseComponent();
  }
}
